// src/lib/producerApi.ts
//
// The free producer account (beta): the member's own switch and business details under
// Profile Home. The api is Controllers/ProducerController.cs (GET and PUT /api/producer/me,
// signed in). Calls go through the same-origin proxy with the session token, the way the other
// /profile forms do (LeaderboardOptInToggle, MessagePreferencesForm).
//
// The Producer label is a self-declaration. It is not an ID check, and it never stands in for
// the Verified badge. Under the label rule the business name, website, state and what they
// produce are public on the profile (the api sends them on the person read), and the business
// name shows on the producers list card.
//
// Turning on asks one required question, the role: "producer" (producer only: the producers list
// and not talent search) or "producer_and_talent" (both). Nothing is preselected: the api reports
// the role only while the account is on, so turning on again starts with no answer.

import { apiFetch } from '@/lib/api';
import { STATE_SLUGS } from '@/lib/search/geo-data';
import { producerRoleOf, type ProducerRole } from '@/lib/producerLabel';

const ME = '/api/proxy/api/producer/me';

export type ProducerIneligibleReason = 'no_profile' | 'profile_not_approved' | 'disabled_by_admin';

export type ProducerMe = {
  isProducer: boolean;
  eligible: boolean;
  ineligibleReason: ProducerIneligibleReason | null;
  businessName: string | null;
  website: string | null;
  state: string | null;
  produces: string | null;
  /** The member's answer while the account is on; null while it is off. */
  role: ProducerRole | null;
};

export type ProducerUpdate = {
  isProducer: boolean;
  businessName?: string | null;
  website?: string | null;
  state?: string | null;
  produces?: string | null;
  role?: ProducerRole;
};

export type ProducerField = 'businessName' | 'website' | 'state' | 'produces' | 'role';

export const MAX_BUSINESS_NAME = 120;
export const MAX_WEBSITE = 200;
export const MAX_PRODUCES = 140;

/** The api's field error codes, as the form shows them. */
export const FIELD_ERROR_TEXT: Record<string, string> = {
  business_name_required: 'Enter your business name.',
  business_name_too_long: 'Use 120 characters or fewer.',
  website_invalid: 'Enter a full web address starting with http:// or https://.',
  state_invalid: 'Choose a state.',
  produces_too_long: 'Use 140 characters or fewer.',
  role_required: 'Choose how your profile should show.',
  role_invalid: 'Choose how your profile should show.',
};

/** Which field each api error code belongs to, for a 400 that names no field. */
const FIELD_OF_CODE: Record<string, ProducerField> = {
  business_name_required: 'businessName',
  business_name_too_long: 'businessName',
  website_invalid: 'website',
  state_invalid: 'state',
  produces_too_long: 'produces',
  role_required: 'role',
  role_invalid: 'role',
};

/** The 50 states and DC, the codes the api accepts, sorted by name. */
export const US_STATE_OPTIONS: ReadonlyArray<{ code: string; name: string }> = Object.values(STATE_SLUGS)
  .map(({ code, name }) => ({ code, name }))
  .sort((a, b) => a.name.localeCompare(b.name));

/**
 * A refused call. `code` is the api's { error } code (profile_not_approved, disabled_by_admin,
 * no_profile, or a field code); `field` is set on a field error.
 */
export class ProducerError extends Error {
  code: string;
  field: ProducerField | null;
  status: number;
  constructor(code: string, status: number, field: ProducerField | null = null) {
    super(code);
    this.name = 'ProducerError';
    this.code = code;
    this.status = status;
    this.field = field;
  }
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

function normalizeReason(v: unknown): ProducerIneligibleReason | null {
  return v === 'no_profile' || v === 'profile_not_approved' || v === 'disabled_by_admin' ? v : null;
}

/** The api's GET shape, read defensively (strings null when unset). */
export function normalizeProducerMe(j: any): ProducerMe {
  return {
    isProducer: j?.isProducer === true,
    eligible: j?.eligible === true,
    ineligibleReason: normalizeReason(j?.ineligibleReason),
    businessName: str(j?.businessName),
    website: str(j?.website),
    state: str(j?.state),
    produces: str(j?.produces),
    role: producerRoleOf(j?.role),
  };
}

async function readError(res: Response): Promise<ProducerError> {
  let code = 'request_failed';
  let field: ProducerField | null = null;
  try {
    const j: any = await res.json();
    if (typeof j?.error === 'string' && j.error) code = j.error;
    const f = j?.field;
    if (f === 'businessName' || f === 'website' || f === 'state' || f === 'produces' || f === 'role') field = f;
  } catch { /* not json */ }
  if (!field && FIELD_OF_CODE[code]) field = FIELD_OF_CODE[code];
  return new ProducerError(code, res.status, field);
}

export async function getProducerMe(): Promise<ProducerMe> {
  // Two-arg form: apiFetch(path, undefined, init) drops init (see api.ts).
  const res = await apiFetch(ME, { method: 'GET', cache: 'no-store' });
  if (!res.ok) throw await readError(res);
  return normalizeProducerMe(await res.json());
}

export async function saveProducerMe(body: ProducerUpdate): Promise<ProducerMe> {
  // Two-arg form: the three-arg form apiFetch(ME, undefined, init) dropped init, so every save
  // went out as a bare GET, the api answered with the unchanged account, and nothing was saved.
  const res = await apiFetch(ME, { method: 'PUT', body: JSON.stringify(body) });
  if (!res.ok) throw await readError(res);
  return normalizeProducerMe(await res.json());
}

/** An absolute http or https address, as the api checks it. */
export function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return (u.protocol === 'http:' || u.protocol === 'https:') && !!u.hostname;
  } catch {
    return false;
  }
}

// A plain web address: labels of letters, digits and hyphens with at least one dot, an optional
// port, then an optional path, query or fragment. No spaces and no @ anywhere, so a sentence or an
// email address never gains https:// and meets the usual check as typed.
const PLAIN_WEB_ADDRESS = /^[a-z0-9-]+([.][a-z0-9-]+)+(:[0-9]+)?([/?#][^\s@]*)?$/i;

/**
 * The website as a save sends it: trimmed, with https:// in front of a plain web address typed
 * without a scheme (agaveis.com, www.agaveis.com/casting), so that address saves instead of
 * failing the check. An address already starting with http:// or https:// in any letter case,
 * and anything else, stays as typed.
 */
export function normalizeWebsite(value: string): string {
  const site = value.trim();
  const lower = site.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) return site;
  // Only when the result passes the check: a typo such as agaveis.com:80800 stays as typed, so the
  // field never shows https:// next to an error asking for it.
  const withScheme = `https://${site}`;
  return PLAIN_WEB_ADDRESS.test(site) && withScheme.length <= MAX_WEBSITE && isHttpUrl(withScheme) ? withScheme : site;
}

export type ProducerForm = {
  businessName: string;
  website: string;
  state: string;
  produces: string;
  /** '' until the member picks one; never preselected. */
  role: ProducerRole | '';
};

/**
 * The form's own checks before a save that turns the account on (the api checks the same and
 * stays the authority). Returns the api's error code per field.
 */
export function validateProducerForm(f: ProducerForm): Partial<Record<ProducerField, string>> {
  const errors: Partial<Record<ProducerField, string>> = {};
  const name = f.businessName.trim();
  if (name.length === 0) errors.businessName = 'business_name_required';
  else if (name.length > MAX_BUSINESS_NAME) errors.businessName = 'business_name_too_long';
  const site = f.website.trim();
  if (site.length > 0 && (site.length > MAX_WEBSITE || !isHttpUrl(site))) errors.website = 'website_invalid';
  const st = f.state.trim().toUpperCase();
  if (st.length > 0 && !US_STATE_OPTIONS.some((s) => s.code === st)) errors.state = 'state_invalid';
  if (f.produces.trim().length > MAX_PRODUCES) errors.produces = 'produces_too_long';
  if (!producerRoleOf(f.role)) errors.role = 'role_required';
  return errors;
}

/** The PUT body for turning the account on: trimmed, empty optional fields sent as null. */
export function producerOnBody(f: ProducerForm): ProducerUpdate {
  const opt = (s: string) => (s.trim().length > 0 ? s.trim() : null);
  return {
    isProducer: true,
    businessName: f.businessName.trim(),
    website: opt(f.website),
    state: f.state.trim() ? f.state.trim().toUpperCase() : null,
    produces: opt(f.produces),
    role: producerRoleOf(f.role) ?? undefined,
  };
}
